import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const AddPost = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // ── Posts list ──
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);

  // ── Add-post form ──
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  // ── View dialog ──
  const [viewPost, setViewPost] = useState(null);

  /* ── Fetch all posts ── */
  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      const res = await fetch("https://backend.jcgn.org/adminPosts/getAll");
      const data = await res.json();
      if (data.status) {
        setPosts((data.result || []).sort((a, b) => b.id - a.id));
      }
    } catch {
      // silent
    } finally {
      setPostsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  /* ── Image handlers ── */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setErrors((p) => ({ ...p, image: "" }));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* ── Validation ── */
  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required.";
    if (!description.trim()) e.description = "Description is required.";
    if (!imageFile) e.image = "Please select an image.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("description", description.trim());
      formData.append("image", imageFile);

      const res = await fetch("https://backend.jcgn.org/adminPosts/upload", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (data.status) {
        await Swal.fire({
          icon: "success",
          title: "Post Created!",
          text: "The admin post was created successfully.",
          background: colors.primary[400],
          color: colors.grey[100],
          confirmButtonColor: "#6870fa",
        });
        setTitle("");
        setDescription("");
        setImageFile(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setErrors({});
        fetchPosts();
      } else {
        throw new Error(data.message || "Failed to create post.");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err.message || "Something went wrong. Please try again.",
        background: colors.primary[400],
        color: colors.grey[100],
        confirmButtonColor: "#6870fa",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async (post) => {
    const confirm = await Swal.fire({
      title: "Delete Post?",
      text: `"${post.title}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: colors.grey[600],
      confirmButtonText: "Yes, delete it!",
      background: colors.primary[400],
      color: colors.grey[100],
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(
        `https://backend.jcgn.org/adminPosts/deleteById/${post.id}`,
        { method: "DELETE" }
      );
      const data = await res.json();
      if (data.status) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Post has been deleted.",
          background: colors.primary[400],
          color: colors.grey[100],
          confirmButtonColor: "#6870fa",
          timer: 1800,
          showConfirmButton: false,
        });
        setPosts((prev) => prev.filter((p) => p.id !== post.id));
      } else {
        throw new Error(data.message || "Delete failed.");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err.message,
        background: colors.primary[400],
        color: colors.grey[100],
        confirmButtonColor: "#6870fa",
      });
    }
  };

  /* ── Shared input styles ── */
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: colors.grey[600] },
      "&:hover fieldset": { borderColor: colors.grey[400] },
      "&.Mui-focused fieldset": { borderColor: "#6870fa" },
      backgroundColor: colors.primary[500],
    },
    "& .MuiInputLabel-root": { color: colors.grey[400] },
    "& .MuiInputLabel-root.Mui-focused": { color: "#6870fa" },
    "& .MuiOutlinedInput-input": { color: colors.grey[100] },
  };

  /* ══════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════ */
  return (
    <Box m="20px">
      {/* ── Page header ── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="-10px"
      >
        <Header title="Posts Management" subtitle="Create and manage admin posts" />
        <Chip
          label={`${posts.length} Post${posts.length !== 1 ? "s" : ""}`}
          sx={{
            backgroundColor: colors.greenAccent[700],
            color: colors.grey[100],
            fontWeight: "bold",
            fontSize: "13px",
          }}
        />
      </Box>

      {/* ════════════════════════════════════════
          ADD POST FORM
      ════════════════════════════════════════ */}
      <Box
        mt="30px"
        p="28px 30px"
        borderRadius="8px"
        backgroundColor={colors.primary[400]}
        mb="30px"
      >
        <Box display="flex" alignItems="center" gap={1} mb={2.5}>
          <AddIcon sx={{ color: "#6870fa" }} />
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>
            Add New Post
          </Typography>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          gap={3}
        >
          {/* Left: title + description */}
          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              label="Title"
              variant="outlined"
              fullWidth
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors((p) => ({ ...p, title: "" }));
              }}
              error={!!errors.title}
              helperText={errors.title}
              sx={inputSx}
            />
            <TextField
              label="Description"
              variant="outlined"
              fullWidth
              multiline
              rows={5}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description)
                  setErrors((p) => ({ ...p, description: "" }));
              }}
              error={!!errors.description}
              helperText={errors.description}
              sx={inputSx}
            />
          </Box>

          {/* Right: image upload */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2" color={colors.grey[300]} fontWeight="600">
              Post Image
            </Typography>

            {!imagePreview ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: `2px dashed ${errors.image ? "#f44336" : colors.grey[600]}`,
                  borderRadius: "8px",
                  flex: 1,
                  minHeight: 165,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  cursor: "pointer",
                  backgroundColor: colors.primary[500],
                  transition: "border-color 0.2s, background-color 0.2s",
                  "&:hover": {
                    borderColor: "#6870fa",
                    backgroundColor: colors.primary[300],
                  },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 44, color: colors.grey[500] }} />
                <Typography variant="body2" color={colors.grey[300]}>
                  Click to upload image
                </Typography>
                <Typography variant="caption" color={colors.grey[500]}>
                  PNG, JPG, JPEG supported
                </Typography>
              </Box>
            ) : (
              <Box position="relative" flex={1}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    width: "100%",
                    height: "100%",
                    minHeight: 165,
                    maxHeight: 220,
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: `1px solid ${colors.grey[600]}`,
                    display: "block",
                  }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    backgroundColor: "rgba(0,0,0,0.65)",
                    color: "white",
                    "&:hover": { backgroundColor: "#f44336" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {errors.image && (
              <Typography variant="caption" color="#f44336">
                {errors.image}
              </Typography>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleImageChange}
            />
          </Box>
        </Box>

        {/* Submit row */}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={17} sx={{ color: "white" }} />
              ) : (
                <AddIcon />
              )
            }
            sx={{
              backgroundColor: "#6870fa",
              color: "white",
              px: 4,
              fontSize: "15px",
              "&:hover": { backgroundColor: "#3e4396" },
              "&:disabled": { backgroundColor: "#3e4396", opacity: 0.7 },
            }}
          >
            {submitting ? "Creating..." : "Create Post"}
          </Button>
        </Box>
      </Box>

      {/* ════════════════════════════════════════
          POSTS GRID
      ════════════════════════════════════════ */}
      <Box>
        <Typography variant="h6" fontWeight="bold" color={colors.grey[100]} mb={2}>
          All Posts
        </Typography>

        {postsLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress sx={{ color: "#6870fa" }} />
          </Box>
        ) : posts.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            backgroundColor={colors.primary[400]}
            borderRadius="8px"
          >
            <ImageNotSupportedIcon
              sx={{ fontSize: 56, color: colors.grey[600], mb: 1.5 }}
            />
            <Typography color={colors.grey[400]} variant="h6">
              No posts yet
            </Typography>
            <Typography color={colors.grey[600]} variant="body2">
              Create your first post using the form above.
            </Typography>
          </Box>
        ) : (
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
              lg: "repeat(4, 1fr)",
            }}
            gap={2.5}
          >
            {posts.map((post) => (
              <Card
                key={post.id}
                sx={{
                  backgroundColor: colors.primary[400],
                  backgroundImage: "none",
                  borderRadius: "8px",
                  overflow: "hidden",
                  border: `1px solid ${colors.grey[700]}`,
                  transition: "transform 0.18s, box-shadow 0.18s",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                  },
                }}
              >
                {post.image_url ? (
                  <CardMedia
                    component="img"
                    height="160"
                    image={post.image_url}
                    alt={post.title}
                    sx={{ objectFit: "cover" }}
                  />
                ) : (
                  <Box
                    height={160}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor={colors.primary[500]}
                  >
                    <ImageNotSupportedIcon
                      sx={{ fontSize: 48, color: colors.grey[600] }}
                    />
                  </Box>
                )}

                <CardContent sx={{ p: "14px 16px 12px" }}>
                  <Chip
                    label={`#${post.id}`}
                    size="small"
                    sx={{
                      backgroundColor: colors.greenAccent[700],
                      color: colors.grey[100],
                      fontWeight: "bold",
                      fontSize: "11px",
                      mb: 1,
                      height: 20,
                    }}
                  />

                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={colors.grey[100]}
                    noWrap
                    title={post.title}
                  >
                    {post.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color={colors.grey[400]}
                    sx={{
                      mt: 0.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      lineHeight: 1.45,
                      minHeight: "2.9em",
                    }}
                  >
                    {post.description}
                  </Typography>

                  <Typography
                    variant="caption"
                    color={colors.grey[600]}
                    display="block"
                    mt={1}
                  >
                    {formatDate(post.createdAt)}
                  </Typography>

                  <Box display="flex" gap={1} mt={1.5}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => setViewPost(post)}
                        sx={{
                          color: colors.grey[300],
                          "&:hover": {
                            color: "#6870fa",
                            backgroundColor: "rgba(104,112,250,0.1)",
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Post">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(post)}
                        sx={{
                          color: colors.grey[400],
                          "&:hover": {
                            color: "#f44336",
                            backgroundColor: "rgba(244,67,54,0.1)",
                          },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* ════════════════════════════════════════
          VIEW DIALOG
      ════════════════════════════════════════ */}
      <Dialog
        open={!!viewPost}
        onClose={() => setViewPost(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            backgroundImage: "none",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.greenAccent[700],
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            px: 2.5,
          }}
        >
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>
            Post Details
          </Typography>
          <IconButton
            onClick={() => setViewPost(null)}
            size="small"
            sx={{ color: colors.grey[100] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: "20px !important", px: 3, pb: 3 }}>
          {viewPost && (
            <Box display="flex" flexDirection="column" gap={2}>
              {viewPost.image_url ? (
                <Box
                  component="img"
                  src={viewPost.image_url}
                  alt={viewPost.title}
                  sx={{
                    width: "100%",
                    maxHeight: 260,
                    objectFit: "cover",
                    borderRadius: "6px",
                    border: `1px solid ${colors.grey[600]}`,
                  }}
                />
              ) : (
                <Box
                  height={160}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor={colors.primary[500]}
                  borderRadius="6px"
                >
                  <ImageNotSupportedIcon
                    sx={{ fontSize: 52, color: colors.grey[600] }}
                  />
                </Box>
              )}

              {[
                { label: "ID", value: `#${viewPost.id}` },
                { label: "Title", value: viewPost.title },
                { label: "Description", value: viewPost.description },
                { label: "Created", value: formatDate(viewPost.createdAt) },
                { label: "Updated", value: formatDate(viewPost.updatedAt) },
              ].map((item) => (
                <Box
                  key={item.label}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  borderBottom={`1px solid ${colors.grey[700]}`}
                  pb={1}
                  gap={2}
                >
                  <Typography
                    variant="body2"
                    color={colors.grey[400]}
                    fontWeight="600"
                    flexShrink={0}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    variant="body2"
                    color={colors.grey[100]}
                    textAlign="right"
                  >
                    {item.value}
                  </Typography>
                </Box>
              ))}

              <Box display="flex" gap={1.5} mt={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setViewPost(null)}
                  sx={{
                    borderColor: colors.grey[500],
                    color: colors.grey[300],
                    "&:hover": { borderColor: colors.grey[300] },
                  }}
                >
                  Close
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => {
                    setViewPost(null);
                    handleDelete(viewPost);
                  }}
                  sx={{
                    backgroundColor: "#f44336",
                    color: "white",
                    "&:hover": { backgroundColor: "#c62828" },
                  }}
                >
                  Delete
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AddPost;